export interface TruncateHtmlOptions {
  keepImageTag?: boolean;
  truncateLastWord?: boolean;
  slop?: number;
  ellipsis?: string | boolean;
}

export function truncateHtml(
  input: string,
  maxLength: number,
  options: TruncateHtmlOptions = {},
): string {
  const DEFAULT_ELLIPSIS = '...';
  const DEFAULT_SLOP = Math.min(10, maxLength);
  const EXCLUDE_TAGS = ['img', 'br'];

  const SELF_CLOSE_REGEX = /<\w+(?:\s+[\w:-]+="[^"]*")*\s*\/>/;
  const HTML_TAG_REGEX = /<\/?\w+(?:\s+[\w:-]+="[^"]*")*\s*\/?>/g;
  const URL_REGEX =
    /(((ftp|https?):\/\/)[\-\w@:%_\+.~#?,&//=]+)|((mailto:)?[_.\w\-]+@([\w][\w\-]+\.)+[a-zA-Z]{2,3})/g;
  const IMAGE_TAG_REGEX = /<img(?:\s+[\w:-]+="[^"]*")*\s*\/?>/g;
  const WORD_BREAK_REGEX = /\W+/g;

  let totalVisible = 0;
  let content = '';
  let tags: string[] = [];

  options.ellipsis = options.ellipsis ?? DEFAULT_ELLIPSIS;
  options.truncateLastWord = options.truncateLastWord ?? true;
  options.slop = options.slop ?? DEFAULT_SLOP;

  function removeImageTags(str: string): string {
    return str.replace(IMAGE_TAG_REGEX, '');
  }

  function closeOpenTags(openTags: string[]): string {
    return openTags
      .slice()
      .reverse()
      .map((tag) => (EXCLUDE_TAGS.includes(tag) ? '' : `</${tag}>`))
      .join('');
  }

  function getTagName(tag: string): string {
    const match = /^<\/?(\w+)/.exec(tag);
    if (!match) {
      throw new Error(`Malformed tag: ${tag}`);
    }
    return match[1];
  }

  function getEndPosition(str: string, tailPos?: number): number {
    const defaultPos = maxLength - totalVisible;
    const isShort = defaultPos < options.slop!;
    const slopPos = isShort ? defaultPos : options.slop! - 1;

    const start = isShort ? 0 : defaultPos - options.slop!;
    const end = tailPos ?? defaultPos + options.slop!;

    const slice = str.slice(start, end);

    if (!options.truncateLastWord) {
      let match: RegExpExecArray | null;
      while ((match = WORD_BREAK_REGEX.exec(slice)) !== null) {
        const idx = match.index;
        if (idx < slopPos) {
          return defaultPos - (slopPos - idx);
        } else if (idx === slopPos) {
          return defaultPos;
        } else {
          return defaultPos + (idx - slopPos);
        }
      }
    }

    return defaultPos;
  }

  while (input) {
    const tagMatch = HTML_TAG_REGEX.exec(input);
    if (!tagMatch) {
      if (totalVisible >= maxLength) break;

      const urlMatch = URL_REGEX.exec(input);
      if (!urlMatch || urlMatch.index >= maxLength) {
        const end = getEndPosition(input);
        content += input.substring(0, end);
        totalVisible += end;
        break;
      }

      while (urlMatch) {
        const { 0: match, index } = urlMatch;
        const visiblePart = input.substring(0, index + match.length);
        content += visiblePart;
        totalVisible += visiblePart.length;
        input = input.substring(index + match.length);
        URL_REGEX.lastIndex = 0;
      }

      break;
    }

    const tag = tagMatch[0];
    const index = tagMatch.index;

    if (totalVisible + index > maxLength) {
      const cut = getEndPosition(input, index);
      content += input.substring(0, cut);
      totalVisible += cut;
      break;
    }

    totalVisible += index;
    content += input.substring(0, index);

    if (tag.startsWith('</')) {
      tags.pop();
    } else {
      const isSelfClose = SELF_CLOSE_REGEX.test(tag);
      const tagName = getTagName(tag);
      if (!isSelfClose) tags.push(tagName);
    }

    content += tag;
    input = input.substring(index + tag.length);
    HTML_TAG_REGEX.lastIndex = 0;
  }

  if (totalVisible >= maxLength && options.ellipsis) {
    content += options.ellipsis;
  }

  content += closeOpenTags(tags);

  if (!options.keepImageTag) {
    content = removeImageTags(content);
  }

  return content;
}
